import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, KeyRound, Lock, Trash2, ShieldCheck, Loader2, Pencil, ShieldAlert, ShieldOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MODULES, ALL_MODULE_IDS, SUB_PERMISSIONS, OWNER_EMAIL } from "@/lib/permissions";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "accountant", label: "Accountant" },
];

const emptyDraft = () => ({
  email: "", name: "", role: "admin", password: "", pin: "",
  permissions: [...ALL_MODULE_IDS],   // new users start with full access ticked
  totp_required: true,                // 2FA on by default; owner can flip it off per user
});

/** Compact checkbox grid used both in the create form and the edit row. */
function PermissionPicker({ value, onChange, disabled = false, testidPrefix }) {
  const list = value || [];
  const allOn = ALL_MODULE_IDS.every((id) => list.includes(id));
  const toggle = (id) => {
    if (disabled) return;
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    onChange(next);
  };
  const toggleAll = () => {
    if (disabled) return;
    // "Tick all" = grant every top-level module (skip scoped sub-permissions,
    // they're mutually exclusive with the parent).
    onChange(allOn ? [] : [...ALL_MODULE_IDS]);
  };
  const grantedTop = list.filter((id) => ALL_MODULE_IDS.includes(id)).length;
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-picker`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-600">
          {allOn ? "Full portal access" : grantedTop === 0 ? "No access to any section" : `${grantedTop} of ${ALL_MODULE_IDS.length} sections`}
        </div>
        <button
          type="button"
          data-testid={`${testidPrefix}-toggle-all`}
          onClick={toggleAll}
          disabled={disabled}
          className="text-[11px] uppercase tracking-wider font-semibold text-orange-600 hover:text-orange-700 disabled:text-slate-300"
        >
          {allOn ? "Untick all" : "Tick all"}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {MODULES.map((m) => {
          const on = list.includes(m.id);
          const subs = SUB_PERMISSIONS.filter((s) => s.parent === m.id);
          return (
            <div key={m.id} className="space-y-1.5">
              <label
                data-testid={`${testidPrefix}-${m.id}`}
                className={`flex items-start gap-2 px-2.5 py-1.5 rounded-sm border text-xs cursor-pointer select-none transition ${
                  on ? "border-orange-400 bg-orange-50" : "border-slate-200 bg-white hover:border-slate-300"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(m.id)}
                  disabled={disabled}
                  className="mt-0.5 h-3.5 w-3.5 rounded-sm accent-orange-500"
                />
                <span className="min-w-0">
                  <span className={`block font-medium ${on ? "text-slate-900" : "text-slate-700"}`}>{m.label}</span>
                  <span className="block text-[10.5px] text-slate-500 leading-snug">{m.hint}</span>
                </span>
              </label>
              {subs.map((s) => {
                const subOn = list.includes(s.id);
                // Sub-permission is only meaningful when the parent is OFF; when the
                // parent is ON we show it disabled with a "covered by full access"
                // hint so people can still find it in the picker.
                const coveredByParent = on;
                return (
                  <label
                    key={s.id}
                    data-testid={`${testidPrefix}-${s.id}`}
                    className={`ml-4 flex items-start gap-2 px-2.5 py-1.5 rounded-sm border-l-2 text-[11px] select-none transition ${
                      subOn && !coveredByParent ? "border-orange-400 bg-orange-50/80" : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                    } ${coveredByParent || disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                    title={coveredByParent ? "Already covered by full Clients access — untick Clients first to use this scoped option." : ""}
                  >
                    <input
                      type="checkbox"
                      checked={subOn && !coveredByParent}
                      onChange={() => toggle(s.id)}
                      disabled={disabled || coveredByParent}
                      className="mt-0.5 h-3.5 w-3.5 rounded-sm accent-orange-500"
                    />
                    <span className="min-w-0">
                      <span className={`block font-medium ${subOn && !coveredByParent ? "text-slate-900" : "text-slate-700"}`}>{s.label}</span>
                      <span className="block text-[10.5px] text-slate-500 leading-snug">
                        {coveredByParent ? "Already covered by full Clients access." : s.hint}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const isOwner = (me?.email || "").toLowerCase() === OWNER_EMAIL;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());

  const load = async () => {
    if (!isOwner) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get("/users");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [isOwner]);

  if (!isOwner) {
    return (
      <div data-testid="users-page-denied" className="max-w-xl">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Settings · Users</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Restricted area</h1>
        <p className="text-sm text-slate-600 mt-3">User management is only available to the owner account (<span className="font-medium">{OWNER_EMAIL}</span>). Please ask the owner to make any changes here.</p>
      </div>
    );
  }

  const createUser = async () => {
    if (!draft.email.trim() || !draft.name.trim() || draft.password.length < 8) {
      toast.error("Email, name and a password of 8+ characters are required.");
      return;
    }
    if (draft.pin && !/^\d{4,6}$/.test(draft.pin)) {
      toast.error("PIN must be 4–6 digits.");
      return;
    }
    setCreating(true);
    try {
      // Send null (= unrestricted) only when EVERY top-level module is ticked, so
      // adding new modules later doesn't accidentally lock this user out.
      const allTop = ALL_MODULE_IDS.every((id) => draft.permissions.includes(id));
      const perms = allTop ? null : draft.permissions;
      await api.post("/users", {
        email: draft.email.trim().toLowerCase(),
        name: draft.name.trim(),
        role: draft.role,
        password: draft.password,
        pin: draft.pin || null,
        permissions: perms,
        totp_required: draft.totp_required,
      });
      toast.success("User added");
      setDraft(emptyDraft());
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  return (
    <div data-testid="users-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Settings · Users</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">User management</h1>
        <p className="text-sm text-slate-600 mt-1">Add, remove and manage staff accounts. Choose which portal sections each user can see — untick a section to hide it from their sidebar and block them from opening it directly.</p>
      </div>

      {/* Add user */}
      <Card data-testid="add-user-card" className="rounded-sm border border-slate-200 shadow-none p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-4 h-4 text-orange-500" />
          <h2 className="font-display text-base font-medium text-slate-900">Add a new user</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Full name</Label>
            <Input data-testid="user-new-name" value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Haseeb" className="mt-1 rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Email</Label>
            <Input data-testid="user-new-email" type="email" value={draft.email} onChange={(e) => setDraft(d => ({ ...d, email: e.target.value }))} placeholder="name@d4pslogistics.com" className="mt-1 rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Role</Label>
            <Select value={draft.role} onValueChange={(v) => setDraft(d => ({ ...d, role: v }))}>
              <SelectTrigger data-testid="user-new-role" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Password</Label>
            <Input data-testid="user-new-password" type="text" value={draft.password} onChange={(e) => setDraft(d => ({ ...d, password: e.target.value }))} placeholder="Min 8 chars" className="mt-1 rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">PIN (4–6 digits)</Label>
            <Input data-testid="user-new-pin" inputMode="numeric" value={draft.pin} onChange={(e) => setDraft(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="optional" className="mt-1 rounded-sm border-slate-300 tabular-nums tracking-widest" />
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-3.5 h-3.5 text-orange-500" />
            <Label className="text-xs text-slate-700 font-semibold uppercase tracking-wider">Portal access</Label>
          </div>
          <PermissionPicker
            value={draft.permissions}
            onChange={(next) => setDraft(d => ({ ...d, permissions: next }))}
            testidPrefix="user-new-perm"
          />
        </div>

        <div className="mt-5 pt-5 border-t border-slate-200">
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="user-new-2fa-row">
            <input
              type="checkbox"
              data-testid="user-new-2fa"
              checked={!!draft.totp_required}
              onChange={(e) => setDraft(d => ({ ...d, totp_required: e.target.checked }))}
              className="mt-1 h-4 w-4 rounded-sm border-slate-300 accent-orange-500"
            />
            <div>
              <div className="text-sm font-semibold text-slate-800">Require two-factor authentication</div>
              <div className="text-xs text-slate-500 mt-0.5">
                On (recommended): this user must scan a TOTP QR the first time they log in and enter a 6-digit code every login. Off: they log in with just password + PIN.
              </div>
            </div>
          </label>
        </div>

        <div className="mt-5 flex justify-end">
          <Button data-testid="user-new-save" onClick={createUser} disabled={creating} className="rounded-sm bg-slate-900 hover:bg-slate-800">
            {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
            Add user
          </Button>
        </div>
      </Card>

      {/* List */}
      <Card data-testid="users-list-card" className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <Th>User</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Access</Th>
                <Th>PIN</Th>
                <Th>2FA</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-10 text-slate-500 text-sm">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500 text-sm">No users yet.</td></tr>}
              {!loading && rows.map((u) => (
                <UserRow key={u.id} u={u} me={me} onChange={load} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`text-left text-[11px] uppercase tracking-wider font-semibold px-3 py-2.5 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-3 align-middle ${className}`}>{children}</td>; }

function AccessBadge({ perms, isOwnerRow }) {
  if (isOwnerRow) {
    return (
      <Badge className="rounded-sm bg-slate-900 text-white hover:bg-slate-900 border border-slate-900" data-testid="access-badge-owner">
        Owner · full
      </Badge>
    );
  }
  if (perms == null) {
    return (
      <Badge className="rounded-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200" data-testid="access-badge-full">
        Full access
      </Badge>
    );
  }
  if (perms.length === 0) {
    return (
      <Badge className="rounded-sm bg-rose-100 text-rose-800 hover:bg-rose-100 border border-rose-200" data-testid="access-badge-none">
        <ShieldAlert className="w-3 h-3 mr-1" /> No access
      </Badge>
    );
  }
  return (
    <Badge className="rounded-sm bg-amber-50 text-amber-800 hover:bg-amber-50 border border-amber-200" data-testid="access-badge-partial">
      {perms.length} / {ALL_MODULE_IDS.length} sections
    </Badge>
  );
}

function UserRow({ u, me, onChange }) {  const isMe = me?.id === u.id;
  const isOwnerRow = (u.email || "").toLowerCase() === OWNER_EMAIL;
  const [editName, setEditName] = useState(false);
  const [name, setName] = useState(u.name || "");
  const [role, setRole] = useState(u.role || "admin");
  const [newPwd, setNewPwd] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [permsOpen, setPermsOpen] = useState(false);
  // Local editing copy — starts from what's on the record (null = full access = all ticked).
  const [perms, setPerms] = useState(u.permissions == null ? [...ALL_MODULE_IDS] : u.permissions);

  const saveName = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${u.id}`, { name, role });
      toast.success("Saved");
      setEditName(false);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const changeRole = async (v) => {
    setRole(v);
    try {
      await api.put(`/users/${u.id}`, { role: v });
      toast.success("Role updated");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); setRole(u.role); }
  };

  const savePerms = async () => {
    setBusy(true);
    try {
      const allTop = ALL_MODULE_IDS.every((id) => perms.includes(id));
      const payload = allTop ? null : perms;
      await api.put(`/users/${u.id}`, { permissions: payload });
      toast.success("Access updated");
      setPermsOpen(false);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resetPwd = async () => {
    if (newPwd.length < 8) { toast.error("Password must be 8+ characters."); return; }
    try {
      await api.patch(`/users/${u.id}/password`, { password: newPwd });
      toast.success("Password updated");
      setNewPwd("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPin = async () => {
    if (!/^\d{4,6}$/.test(newPin)) { toast.error("PIN must be 4–6 digits."); return; }
    try {
      await api.patch(`/users/${u.id}/pin`, { pin: newPin });
      toast.success("PIN updated");
      setNewPin("");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reset2fa = async () => {
    if (!window.confirm(`Reset 2FA for ${u.email}?\nThey'll be asked to enrol a new authenticator app on their next sign-in and any trusted devices will be revoked.`)) return;
    setBusy(true);
    try {
      await api.post(`/users/${u.id}/2fa/reset`);
      toast.success("2FA reset — user will re-enrol on next sign-in.");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Flip the "2FA required" flag on/off for this user. Turning it off wipes
  // any pending enrollment on the backend so the very next login is a
  // straight-through pass.
  const toggle2faRequired = async () => {
    const next = !(u.totp_required ?? true);
    const msg = next
      ? `Turn 2FA back ON for ${u.email}?\nThey'll be forced to set up an authenticator on their next sign-in.`
      : `Turn 2FA OFF for ${u.email}?\nThey'll be able to sign in with just password + PIN. Any existing enrollment is wiped.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.put(`/users/${u.id}`, { totp_required: next });
      toast.success(next ? "2FA is now required" : "2FA has been turned off");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <tr className="border-t border-slate-200 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
        <Td>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-sm bg-slate-900 text-white flex items-center justify-center text-[11px] font-semibold">
              {(u.name || u.email || "U").split(" ").map(s => s[0]).join("").slice(0,2).toUpperCase()}
            </div>
            {editName ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 rounded-sm border-slate-300 text-sm" data-testid={`user-name-edit-${u.id}`} />
            ) : (
              <div className="font-medium text-slate-900">{u.name || "—"}{isMe && <span className="ml-2 text-[10px] text-orange-600 font-semibold">(you)</span>}</div>
            )}
          </div>
        </Td>
        <Td><div className="text-slate-700 text-xs tabular-nums">{u.email}</div></Td>
        <Td>
          <Select value={role} onValueChange={changeRole} disabled={isMe}>
            <SelectTrigger className="h-8 rounded-sm border-slate-300 w-36" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </Td>
        <Td>
          <div className="flex items-center gap-2">
            <AccessBadge perms={u.permissions} isOwnerRow={isOwnerRow} />
            {!isOwnerRow && (
              <button
                type="button"
                className="text-[11px] font-medium text-orange-600 hover:text-orange-700"
                onClick={() => { setPerms(u.permissions == null ? [...ALL_MODULE_IDS] : u.permissions); setPermsOpen((v) => !v); }}
                data-testid={`user-perms-toggle-${u.id}`}
              >
                {permsOpen ? "Close" : "Edit"}
              </button>
            )}
          </div>
        </Td>
        <Td>
          {u.has_pin ? (
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"><ShieldCheck className="w-3 h-3 mr-1" />Set</Badge>
          ) : (
            <Badge className="rounded-sm bg-amber-50 text-amber-800 hover:bg-amber-50 border border-amber-200">Not set</Badge>
          )}
        </Td>
        <Td>
          <div className="flex items-center gap-2 flex-wrap">
            {u.has_2fa ? (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200" data-testid={`user-2fa-on-${u.id}`}>
                <Smartphone className="w-3 h-3 mr-1" />Enrolled
              </Badge>
            ) : (
              <Badge className="rounded-sm bg-amber-50 text-amber-800 hover:bg-amber-50 border border-amber-200" data-testid={`user-2fa-off-${u.id}`}>
                Not enrolled
              </Badge>
            )}
            {!isOwnerRow && (() => {
              const required = (u.totp_required ?? true);
              return (
                <button
                  type="button"
                  onClick={toggle2faRequired}
                  disabled={busy}
                  role="switch"
                  aria-checked={required}
                  data-testid={`user-2fa-required-${u.id}`}
                  title={required
                    ? "2FA is REQUIRED for this user — click to turn it OFF"
                    : "2FA is OFF for this user — click to turn it back ON"}
                  className={`inline-flex items-center gap-2 h-7 px-2.5 rounded-sm border text-[11px] font-semibold uppercase tracking-wider transition disabled:opacity-50 ${
                    required
                      ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block w-6 h-3 rounded-sm relative transition-colors ${
                      required ? "bg-emerald-500" : "bg-slate-300"
                    }`}
                  >
                    <span
                      className={`absolute top-[1px] w-2.5 h-2.5 rounded-sm bg-white transition-all ${
                        required ? "left-[13px]" : "left-[1px]"
                      }`}
                    />
                  </span>
                  {required ? "2FA on" : "2FA off"}
                </button>
              );
            })()}
            {!isOwnerRow && (
              <Button
                size="sm"
                variant="outline"
                onClick={reset2fa}
                disabled={busy}
                title={u.has_2fa
                  ? "Reset 2FA — the user must set up a new authenticator on next sign-in"
                  : "Clear any half-finished 2FA setup / trusted devices for this user"}
                data-testid={`user-2fa-reset-${u.id}`}
                className={`h-7 rounded-sm ${u.has_2fa
                  ? "border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 hover:border-rose-300"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                <ShieldOff className="w-3.5 h-3.5 mr-1" /> {u.has_2fa ? "Reset authenticator" : "Clear 2FA"}
              </Button>
            )}
          </div>
        </Td>
        <Td className="text-right pr-4">
          <div className="inline-flex items-center gap-1">
            {editName ? (
              <>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => { setEditName(false); setName(u.name); }}>Cancel</Button>
                <Button size="sm" className="rounded-sm h-8 bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={saveName} data-testid={`user-name-save-${u.id}`}>Save</Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0" title="Edit name" onClick={() => setEditName(true)} data-testid={`user-edit-${u.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
            {!isMe && !isOwnerRow && (
              <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-red-600 hover:bg-red-50" title="Delete user" onClick={remove} data-testid={`user-delete-${u.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </Td>
      </tr>
      {permsOpen && !isOwnerRow && (
        <tr className="border-t border-slate-100 bg-orange-50/40" data-testid={`user-perms-row-${u.id}`}>
          <td colSpan={7} className="px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-orange-500" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">Portal access</span>
            </div>
            <PermissionPicker
              value={perms}
              onChange={setPerms}
              testidPrefix={`user-perms-${u.id}`}
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setPermsOpen(false)} data-testid={`user-perms-cancel-${u.id}`}>Cancel</Button>
              <Button size="sm" className="rounded-sm h-8 bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={savePerms} data-testid={`user-perms-save-${u.id}`}>Save access</Button>
            </div>
          </td>
        </tr>
      )}
      <tr className="border-t border-slate-100 bg-slate-50/40">
        <td colSpan={7} className="px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <Lock className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-slate-600">New password</span>
            <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Min 8 chars" className="h-7 w-44 rounded-sm border-slate-300 text-xs" data-testid={`user-newpwd-${u.id}`} />
            <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={resetPwd} disabled={!newPwd} data-testid={`user-newpwd-save-${u.id}`}>Update</Button>
            <div className="mx-2 h-4 w-px bg-slate-200" />
            <KeyRound className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-slate-600">New PIN</span>
            <Input inputMode="numeric" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="4–6 digits" className="h-7 w-32 rounded-sm border-slate-300 text-xs tabular-nums tracking-widest" data-testid={`user-newpin-${u.id}`} />
            <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={resetPin} disabled={!newPin} data-testid={`user-newpin-save-${u.id}`}>Update</Button>
          </div>
        </td>
      </tr>
    </>
  );
}
